import { type Clerk, type SessionResource } from '@clerk/nextjs/types';

export type SessionTransitionState = {
  phase: 'idle' | 'signing-out' | 'navigating' | 'error';
  navigationExpected: boolean;
  message: string;
  retryAt: number;
  canRecheck: boolean;
};

type SignOutOperation = {
  clerk: Pick<Clerk, 'signOut'>;
  session: SessionResource;
  destination: string;
  confirmed: boolean;
  token?: string;
};

export const SESSION_SIGN_OUT_EVENT = 'ra-session-signed-out';
const idleState: SessionTransitionState = { phase: 'idle', navigationExpected: false, message: '', retryAt: 0, canRecheck: false };

/** Only UI reset hints cross tabs. Authentication always remains a server decision. */
export const announceSessionSignOut = (sessionId: string) => {
  const message = { type: SESSION_SIGN_OUT_EVENT, sessionId, nonce: crypto.randomUUID() };
  try {
    const channel = new BroadcastChannel(SESSION_SIGN_OUT_EVENT);
    channel.postMessage(message);
    channel.close();
  } catch {
    // Storage events cover browsers without BroadcastChannel.
  }
  try {
    localStorage.setItem(SESSION_SIGN_OUT_EVENT, JSON.stringify(message));
    localStorage.removeItem(SESSION_SIGN_OUT_EVENT);
  } catch {
    // Clerk's own session updates still reset other tabs when storage is unavailable.
  }
};

export const isSessionSignOutMessage = (value: unknown, sessionId: string | null | undefined): boolean => {
  if (!sessionId || !value || typeof value !== 'object') {
    return false;
  }
  return 'type' in value && value.type === SESSION_SIGN_OUT_EVENT &&
    'sessionId' in value && value.sessionId === sessionId;
};

/** Per-document coordinator: never persists credentials or application data. */
export class SessionTransition {
  private state = idleState;
  private readonly listeners = new Set<() => void>();
  private operation: SignOutOperation | undefined;
  private pending: Promise<void> | undefined;

  constructor(private readonly effects = {
    request: (token: string) => {
      return fetch('/api/account/session/sign-out', {
        method: 'POST', credentials: 'same-origin', cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` }, signal: AbortSignal.timeout(15_000),
      });
    },
    navigate: (destination: string) => { window.location.replace(destination); },
    announce: announceSessionSignOut,
  }) {}

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  getSnapshot = () => { return this.state; };
  getServerSnapshot = () => { return idleState; };

  expectNavigation = () => {
    this.update({ ...this.state, navigationExpected: true });
  };

  cancelExpectedNavigation = () => {
    if (this.state.phase === 'idle') {
      this.update({ ...this.state, navigationExpected: false });
    }
  };

  navigate = (destination: string) => {
    this.update({ phase: 'navigating', navigationExpected: true, message: '', retryAt: 0, canRecheck: false });
    this.effects.navigate(destination);
  };

  signOut = (clerk: Pick<Clerk, 'session' | 'signOut'>, destination = '/') => {
    if (this.pending) {
      return this.pending;
    }
    if (!clerk.session) {
      this.navigate(destination);
      return Promise.resolve();
    }
    this.operation = { clerk, session: clerk.session, destination, confirmed: false };
    return this.retry();
  };

  retry = (): Promise<void> => {
    if (this.pending) {
      return this.pending;
    }
    if (!this.operation || Date.now() < this.state.retryAt) {
      return Promise.resolve();
    }
    const operation = this.operation;
    this.update({ phase: 'signing-out', navigationExpected: true, message: '', retryAt: 0, canRecheck: false });
    this.pending = this.performSignOut(operation).finally(() => { this.pending = undefined; });
    return this.pending;
  };

  recheck = () => {
    if (this.state.phase === 'error' && this.state.canRecheck) {
      // This is a fresh authentication check, not a claim that sign-out succeeded.
      this.navigate('/account/');
    }
  };

  private async performSignOut(operation: SignOutOperation): Promise<void> {
    let retryAfter = 0;
    try {
      if (!operation.confirmed) {
        // An ambiguous response may have revoked the session already. Reuse the
        // captured credential for idempotent retry; never write it to storage.
        const token = operation.token ?? await operation.session.getToken({ skipCache: true });
        if (token) {
          operation.token = token;
          const response = await this.effects.request(token);
          if (response.status !== 204) {
            if (response.status === 401) {
              operation.token = undefined;
            }
            const header = response.headers.get('Retry-After');
            retryAfter = header && /^\d{1,6}$/.test(header) ? Math.min(3600, Number(header)) : 0;
            throw new Error('Unable to confirm sign-out. Please try again.');
          }
          operation.confirmed = true;
          operation.token = undefined;
          this.effects.announce(operation.session.id);
        } else if (operation.session.status !== 'pending') {
          throw new Error('Unable to verify your session for sign-out. Please try again.');
        }
      }

      // Always target the captured session.
      await operation.clerk.signOut(() => {
        // Suppress Clerk's soft redirect; the coordinator navigates below.
      }, { sessionId: operation.session.id });
      if (!operation.confirmed) {
        this.effects.announce(operation.session.id);
      }
      this.navigate(operation.destination);
    } catch {
      this.update({
        phase: 'error', navigationExpected: true,
        message: operation.confirmed
          ? 'Your session has been revoked. Retry to finish signing out of this browser.'
          : 'Unable to confirm sign-out. Please retry. Your workspace is hidden until sign-out finishes.',
        retryAt: Date.now() + retryAfter * 1000,
        canRecheck: !operation.confirmed,
      });
    }
  }

  private update(state: SessionTransitionState): void {
    this.state = state;
    for (const listener of this.listeners) {
      listener();
    }
  }
}

export const sessionTransition = new SessionTransition();
